import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Container, Button, Spinner, Card, CardBody, Collapse, Table, Row, Col } from "reactstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { MASTER_AE_PATHS } from "../../../../Routes/paths";
import { useAdverseEventApi } from "../api/useAdverseEventApi";
import { useAdverseEventFollowupApi } from "../api/useAdverseEventFollowupApi";
import { useAdverseEventMedicationsApi } from "../api/useAdverseEventMedicationsApi";
import { useAdverseEventAssessmentApi } from "../api/useAdverseEventAssessmentApi";
import {
  showToastMessage,
  DateFormat,
  GetUserData,
} from "../../../../common/service/CommonService";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import {
  getAdverseEventFields,
  DEFAULT_FORM_VALUES,
  mapAdverseEventDataToForm,
  mapFollowupsDataToForm,
  mapMedicationsDataToForm,
  formatUpdateAdverseEventPayload,
} from "../helper/adverseeventconstant";
import { loadCodelistsDetailId, Routeof_Administration_OPTIONS, AE_DIAGNOSIS_OPTIONS } from "api/modules/common.option";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import Reason from "common/utilities/Reason/Reason";
import DeleteModal from "Components/Common/DeleteModal";
import FullPageLoader from "Components/Common/FullPageLoader";
import { BreadcrumbContext } from "Layouts/Header";
import { useVolunteerEnrollmentService } from "modules/Clinical/SubjectEnrollment/api/useVolunteerEnrollment";
import AdverseEventEventDetailsForm from "../components/AdverseEventEventDetailsForm";
import AdverseEventFollowupsForm from "../components/AdverseEventFollowupsForm";
import AdverseEventMedicationsForm from "../components/AdverseEventMedicationsForm";
import AdverseEventPiAssessmentForm from "../components/AdverseEventPiAssessmentForm";
import AdverseEventStepper from "../components/AdverseEventStepper";

type AETab = "event_details" | "followups" | "medications" | "pi_assessment";

const EditAdverseEvent: React.FC = () => {
  document.title = "Edit Adverse Event | ZENTIXS VISTA";
  
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<any>(null);

  const tabParam = searchParams.get("tab") as AETab;
  const activeTab: AETab = ["event_details", "followups", "medications", "pi_assessment"].includes(tabParam)
    ? tabParam
    : "event_details";

  const [isTabChanging, setIsTabChanging] = useState(false);

  useEffect(() => {
    loadCodelistsDetailId([
      "AE_STATUS_OPTIONS",
      "AE_SEVERITY_OPTIONS",
      "AE_CAUSALITY_OPTIONS",
      "AE_ACTION_TAKEN_OPTIONS",
      "AE_REPORT_METHOD_OPTIONS",
      "AE_OCCURRENCE_OPTIONS",
      "AE_OUTCOME_OPTIONS",
      "AE_LIKELIHOOD_OPTIONS",
      "AE_RELATIONSHIP_OPTIONS",
      "AE_PERIOD_OPTIONS",
      "AE_DIAGNOSIS_OPTIONS",
      "Routeof_Administration_OPTIONS",
    ]);
  }, []);

  const setActiveTab = (tab: AETab) => {
    setIsTabChanging(true);
    setSearchParams({ tab }, { replace: true });
    setTimeout(() => {
      setIsTabChanging(false);
    }, 300);
  };



  const [isEventDetailsSaved, setIsEventDetailsSaved] = useState(false);
  const [isFollowupsSaved, setIsFollowupsSaved] = useState(false);

  /* ── Dynamic Accordion State ── */
  const [openFollowups, setOpenFollowups] = useState<Record<number, boolean>>({});
  const [openMedicines, setOpenMedicines] = useState<Record<number, boolean>>({});

  const { SelectByIdQuery, UpdateMutation } =
    useAdverseEventApi({
      adverseeventid: Number(id),
    });

  const [deletedFollowupIds, setDeletedFollowupIds] = useState<number[]>([]);
  const [deletedMedicineIds, setDeletedMedicineIds] = useState<number[]>([]);
  const { SelectQuery: followupQuery, InsertMutation: InsertFollowupMutation, UpdateMutation: UpdateFollowupMutation, DeleteMutation: DeleteFollowupMutation } = useAdverseEventFollowupApi({
    adverseeventid: Number(id),
    enabled: activeTab === "followups" || activeTab === "medications" || activeTab === "pi_assessment",
  });

  const { SelectQuery: medicationsQuery, UpdateMutation: UpdateMedicationsMutation } = useAdverseEventMedicationsApi({
    adverseeventid: Number(id),
    enabled: activeTab === "medications" || activeTab === "pi_assessment",
  });

  const { SelectQuery: assessmentQuery, UpdateMutation: UpdateAssessmentMutation } = useAdverseEventAssessmentApi({
    adverseeventid: Number(id),
    enabled: activeTab === "pi_assessment",
  });
  const [savingCardIndex, setSavingCardIndex] = useState<number | null>(null);

  // Delete Follow-up states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [showDeleteReasonModal, setShowDeleteReasonModal] = useState(false);
  const [followupIdToDelete, setFollowupIdToDelete] = useState<number | null>(null);
  const [followupIndexToRemove, setFollowupIndexToRemove] = useState<number | null>(null);
  const [confirmDeleteCallback, setConfirmDeleteCallback] = useState<(() => void) | null>(null);

  // Delete Medicine state (distinguishes from follow-up delete)
  const [deleteType, setDeleteType] = useState<"followup" | "medicine">("followup");

  const handleDeleteMedicine = async (payload: { id: number | null; reason: string }) => {
    if (!payload.id) return;
    try {
      const medsPayload = {
        adverseeventid: Number(id),
        updatereason: payload.reason,
        deletedmedicineid: Number(payload.id),
      };

      const res: any = await UpdateMedicationsMutation.mutateAsync(medsPayload as any);
      showToastMessage("success", res?.message || "Adverse Event Medicine record discarded successfully!");

      const freshData = await medicationsQuery.refetch();
      if (freshData.data?.data) {
        const mappedMeds = mapMedicationsDataToForm(freshData.data.data, formatToDDMMYYYY);
        replaceMedicines(mappedMeds.medicines || []);
      } else {
        replaceMedicines([]);
      }

      setDeleteModalOpen(false);
      setShowDeleteReasonModal(false);
      setFollowupIdToDelete(null);
      setFollowupIndexToRemove(null);
      setConfirmDeleteCallback(null);
    } catch (err: any) {
      showToastMessage(
        "error",
        err?.response?.data?.message || err?.data?.message || err?.message || err || "Failed to delete medicine"
      );
    }
  };

  const handleDeleteFollowup = async (payload: { id: number | null; reason: string }) => {
    if (!payload.id) return;
    try {
      const res: any = await UpdateFollowupMutation.mutateAsync({
        adverseeventid: Number(id),
        updatereason: payload.reason,
        deletedfollowupid: Number(payload.id),
      });

      showToastMessage("success", res?.message || "Follow-up discarded successfully");

      if (confirmDeleteCallback) {
        confirmDeleteCallback();
      }

      await followupQuery.refetch();

      setDeleteModalOpen(false);
      setShowDeleteReasonModal(false);
      setFollowupIdToDelete(null);
      setFollowupIndexToRemove(null);
      setConfirmDeleteCallback(null);
    } catch (err: any) {
      showToastMessage(
        "error",
        err?.response?.data?.message || err?.data?.message || err?.message || err || "Failed to delete follow-up"
      );
    }
  };

  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    setBreadcrumbItems?.([
      { text: "Adverse Event", link: MASTER_AE_PATHS.LIST },
      { text: "Edit Adverse Event" },
    ]);

    return () => {
      setBreadcrumbItems?.([]);
    };
  }, []);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
    watch,
    clearErrors,
    trigger,
  } = useForm<any>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: "onChange",
  });



  /* ── Dynamic Follow-ups ── */
  const {
    fields: followupFields,
    append: appendFollowup,
    remove: removeFollowup,
    replace: replaceFollowups,
  } = useFieldArray({
    control,
    name: "followups",
  });

  /* ── Dynamic Medicines ── */
  const {
    fields: medicineFields,
    append: appendMedicine,
    remove: removeMedicine,
    replace: replaceMedicines,
  } = useFieldArray({
    control,
    name: "medicines",
  });

  const toggleFollowup = (index: number) => {
    setOpenFollowups((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleAddFollowup = () => {
    const newIndex = followupFields.length;
    appendFollowup({
      followupno: String(newIndex + 1).padStart(2, "0"),
      date: "",
      starttime: "",
      endtime: "",
      bp: "",
      pulse: "",
      respiratoryrate: "",
      temperature: "",
      description: "",
    });
    setOpenFollowups((prev) => ({ ...prev, [newIndex]: true }));
  };

  const toggleAllFollowups = (expand: boolean) => {
    const next: Record<number, boolean> = {};
    followupFields.forEach((_, idx) => {
      next[idx] = expand;
    });
    setOpenFollowups(next);
  };

  const toggleMedicine = (index: number) => {
    setOpenMedicines((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleAddMedicine = () => {
    const newIndex = medicineFields.length;
    appendMedicine({
      brandname: "",
      genericname: "",
      frequency: "",
      route: "",
      startdate: "",
      enddate: "",
      interaction: "",
    });
    setOpenMedicines((prev) => ({ ...prev, [newIndex]: true }));
  };

  const toggleAllMedicines = (expand: boolean) => {
    const next: Record<number, boolean> = {};
    medicineFields.forEach((_, idx) => {
      next[idx] = expand;
    });
    setOpenMedicines(next);
  };

  const selectedStudyId = watch("studycode");

  const onsetDateValue = watch("onsetdate");
  const onsetTimeValue = watch("onsettime");
  const reportingDateValue = watch("reportingdate");
  const reportingTimeValue = watch("reportingtime");
  const lastDoseDateValue = watch("lastdosedate");
  const lastDoseTimeValue = watch("lastdosetime");
  const medicalExamStartValue = watch("medicalexamstart");
  const medicalExamEndValue = watch("medicalexamend");
  const watchedAssociatedSymptoms = watch("associatedsymptoms");
  const diagnosisValue = watch("diagnosis");

  useEffect(() => {
    if (
      onsetDateValue ||
      onsetTimeValue ||
      reportingDateValue ||
      reportingTimeValue ||
      lastDoseDateValue ||
      lastDoseTimeValue ||
      medicalExamStartValue ||
      medicalExamEndValue
    ) {
      trigger("reportingdate");
      trigger("reportingtime");
      trigger("onsettime");
      trigger("lastdosedate");
      trigger("lastdosetime");
      trigger("medicalexamend");
    }
  }, [
    onsetDateValue,
    onsetTimeValue,
    reportingDateValue,
    reportingTimeValue,
    lastDoseDateValue,
    lastDoseTimeValue,
    medicalExamStartValue,
    medicalExamEndValue,
    trigger,
  ]);

  useEffect(() => {
    if (diagnosisValue) {
      const selectedOption = AE_DIAGNOSIS_OPTIONS.find(opt => Number(opt.value) === Number(diagnosisValue));
      const label = selectedOption?.label?.toLowerCase() || "";
      const isRemarkAllowed = label === "other" || label.includes("final");
      if (!isRemarkAllowed) {
        setValue("diagnosisremarks", "");
        clearErrors("diagnosisremarks");
      }
    }
  }, [diagnosisValue, setValue, clearErrors]);

  useEffect(() => {
    if (watchedAssociatedSymptoms === "no") {
      setValue("symptomsremark", "");
      clearErrors("symptomsremark");
    }
  }, [watchedAssociatedSymptoms, setValue, clearErrors]);

  const watchedisadverse = watch("isadverse");
  const watchedTemperatureUnit = watch("temperatureunit");
  const watchedTemperatureLocation = watch("temperaturelocation");

  useEffect(() => {
    if (!watchedTemperatureUnit) {
      setValue("temperaturelocation", "");
      setValue("temperature", "");
      clearErrors("temperaturelocation");
      clearErrors("temperature");
    } else if (!watchedTemperatureLocation) {
      setValue("temperature", "");
      clearErrors("temperature");
    }
  }, [watchedTemperatureUnit, watchedTemperatureLocation, setValue, clearErrors]);

  const data = SelectByIdQuery.data?.data?.[0];

  const formatToDDMMYYYY = (dateStr: string) => {
    if (!dateStr || !dateStr.includes("-")) return dateStr;
    const parts = dateStr.includes("T")
      ? dateStr.split("T")[0].split("-")
      : dateStr.split("-");
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };

  useEffect(() => {
    if (!data) return;

    const mappedFormValues = mapAdverseEventDataToForm(data, formatToDDMMYYYY);
    Object.keys(mappedFormValues).forEach((key) => {
      setValue(key, (mappedFormValues as any)[key]);
    });
  }, [SelectByIdQuery.data, setValue]);

  useEffect(() => {
    if (followupQuery.data?.data && Array.isArray(followupQuery.data.data)) {
      const mappedFollowups = mapFollowupsDataToForm(followupQuery.data.data, formatToDDMMYYYY);
      replaceFollowups(mappedFollowups);
    }
  }, [followupQuery.data, replaceFollowups]);

  useEffect(() => {
    if (medicationsQuery.data?.data) {
      const mappedMeds = mapMedicationsDataToForm(medicationsQuery.data.data, formatToDDMMYYYY);
      if (mappedMeds.resolutiondate) setValue("resolutiondate", mappedMeds.resolutiondate);
      if (mappedMeds.resolutiontime) setValue("resolutiontime", mappedMeds.resolutiontime);
      if (mappedMeds.consumedmedication) setValue("consumedmedication", mappedMeds.consumedmedication);
      replaceMedicines(mappedMeds.medicines || []);
    }
  }, [medicationsQuery.data, setValue, replaceMedicines]);

  useEffect(() => {
    if (assessmentQuery.data?.data) {
      const rec = Array.isArray(assessmentQuery.data.data)
        ? assessmentQuery.data.data[0]
        : assessmentQuery.data.data;
      if (rec) {
        if (rec.severity) setValue("piseverity", Number(rec.severity));
        if (rec.actiontaken) setValue("piactiontaken", Number(rec.actiontaken));
        if (rec.causality) setValue("picausality", Number(rec.causality));
        if (rec.outcomeofevent) setValue("outcomeofevent", Number(rec.outcomeofevent));
        if (rec.likelihood) setValue("likelihood", Number(rec.likelihood));
        if (rec.relationship) setValue("relationship", Number(rec.relationship));

        const diagVal = getValues("diagnosis") || rec.diagnosis;
        const diagRemarks = getValues("diagnosisremarks") || rec.diagnosisremarks;
        const selectedOption = AE_DIAGNOSIS_OPTIONS.find(
          (opt) => Number(opt.value) === Number(diagVal)
        );
        const isFinalSelected =
          selectedOption && selectedOption.label.toLowerCase().includes("final");

        if (rec.finaldiagnosis) {
          setValue("finaldiagnosis", rec.finaldiagnosis);
        } else if (isFinalSelected && diagRemarks) {
          setValue("finaldiagnosis", diagRemarks);
        }
      }
    }
  }, [assessmentQuery.data, setValue, getValues]);

  useEffect(() => {
    if (activeTab === "pi_assessment") {
      const sevVal = getValues("severitygrade");
      const actVal = getValues("actiontaken");
      if (sevVal && !getValues("piseverity")) setValue("piseverity", Number(sevVal));
      if (actVal && !getValues("piactiontaken")) setValue("piactiontaken", Number(actVal));

      const diagVal = getValues("diagnosis");
      const diagRemarks = getValues("diagnosisremarks");
      const selectedOption = AE_DIAGNOSIS_OPTIONS.find(
        (opt) => Number(opt.value) === Number(diagVal)
      );
      const isFinalSelected =
        selectedOption && selectedOption.label.toLowerCase().includes("final");

      if (isFinalSelected && diagRemarks) {
        setValue("finaldiagnosis", diagRemarks);
      }
    }
  }, [activeTab, getValues, setValue]);


  const hasSavedFollowups = useMemo(() => {
    const list = followupQuery.data?.data;
    if (Array.isArray(list) && list.length > 0) {
      return true;
    }
    return (followupFields || []).some((f: any) => !!f?.followupid);
  }, [followupQuery.data, followupFields]);

  const [pendingFollowupIndex, setPendingFollowupIndex] = useState<number | null>(null);

  const handleSaveCard = async (index: number) => {
    const fu = getValues(`followups.${index}`);
    if (!fu) return;

    if (fu.followupid) {
      setPendingFollowupIndex(index);
      setShowReasonModal(true);
    } else {
      setSavingCardIndex(index);
      try {
        const ensureSeconds = (timeStr: string) => {
          if (!timeStr) return null;
          return timeStr.length === 5 ? `${timeStr}:00` : timeStr;
        };

        const followupPayload = {
          followupid: null,
          followupdate: DateFormat(fu.folloupdate) || null,
          starttime: ensureSeconds(fu.starttime),
          endtime: ensureSeconds(fu.endtime),
          bloodpressure: fu.bloodpressure || "",
          pulse: fu.pulse ? Number(fu.pulse) : null,
          respiratoryrate: fu.respiratoryrate ? Number(fu.respiratoryrate) : null,
          temperature: fu.temperature ? Number(fu.temperature) : null,
          temperaturelocation: fu.temperaturelocation || "",
          temperatureunit: fu.temperatureunit || "C",
          description: fu.description || "",
          nextfollowuprequired: fu.nextfollowuprequired === "yes",
        };

        const payload = {
          adverseeventid: Number(id),
          followups: [followupPayload],
        };

        if (index > 0) {
          const parentFu = getValues(`followups.${index - 1}`);
          if (parentFu && parentFu.followupid) {
            const parentPayload = {
              followupid: Number(parentFu.followupid),
              followupdate: DateFormat(parentFu.folloupdate) || null,
              starttime: parentFu.starttime ? (parentFu.starttime.length === 5 ? `${parentFu.starttime}:00` : parentFu.starttime) : null,
              endtime: parentFu.endtime ? (parentFu.endtime.length === 5 ? `${parentFu.endtime}:00` : parentFu.endtime) : null,
              bloodpressure: parentFu.bloodpressure || "",
              pulse: parentFu.pulse ? Number(parentFu.pulse) : null,
              respiratoryrate: parentFu.respiratoryrate ? Number(parentFu.respiratoryrate) : null,
              temperature: parentFu.temperature ? Number(parentFu.temperature) : null,
              temperaturelocation: parentFu.temperaturelocation || "",
              temperatureunit: parentFu.temperatureunit || "C",
              description: parentFu.description || "",
              nextfollowuprequired: true,
            };
            await UpdateFollowupMutation.mutateAsync({
              adverseeventid: Number(id),
              followups: [parentPayload],
            });
          }
        }

        const res: any = await InsertFollowupMutation.mutateAsync({
          adverseeventid: payload.adverseeventid,
          followups: [followupPayload],
        });

        const newFollowupId =
          res?.data?.[0]?.followupid ||
          res?.data?.followupid ||
          res?.followupids?.[0] ||
          res?.data?.followupids?.[0] ||
          res?.data?._followupids?.[0] ||
          res?.id;
        if (newFollowupId) {
          setValue(`followups.${index}.followupid`, newFollowupId);
        }

        if (fu.nextfollowuprequired !== "yes") {
          const localFollowups = getValues("followups") || [];
          for (let i = localFollowups.length - 1; i > index; i--) {
            removeFollowup(i);
          }
        }

        await followupQuery.refetch();
        showToastMessage("success", res?.message || "Follow-up saved successfully");
      } catch (err: any) {
        showToastMessage("error", err?.response?.data?.message || err?.data?.message || err?.message || err || "Failed to save follow-up");
      } finally {
        setSavingCardIndex(null);
      }
    }
  };

  const handleMedicationsSubmit = (data: any) => {
    setPendingFormData(data);
    setCurrentStepToSave(3);
    setShowReasonModal(true);
  };

  const [currentStepToSave, setCurrentStepToSave] = useState<number | null>(null);

  const handleEventDetailsSubmit = (data: any) => {
    setPendingFormData(data);
    setCurrentStepToSave(1);
    setShowReasonModal(true);
  };

  const handlePiAssessmentSubmit = (data: any) => {
    setPendingFormData(data);
    setCurrentStepToSave(4);
    setShowReasonModal(true);
  };

  const handleReasonSubmit = async (payload: {
    id: number | null;
    reason: string;
  }) => {
    if (pendingFollowupIndex !== null) {
      const idx = pendingFollowupIndex;
      setPendingFollowupIndex(null);
      setShowReasonModal(false);
      setSavingCardIndex(idx);
      try {
        const fu = getValues(`followups.${idx}`);
        if (!fu) return;

        const ensureSeconds = (timeStr: string) => {
          if (!timeStr) return null;
          return timeStr.length === 5 ? `${timeStr}:00` : timeStr;
        };

        const followupPayload = {
          followupid: Number(fu.followupid),
          followupdate: DateFormat(fu.folloupdate) || null,
          starttime: ensureSeconds(fu.starttime),
          endtime: ensureSeconds(fu.endtime),
          bloodpressure: fu.bloodpressure || "",
          pulse: fu.pulse ? Number(fu.pulse) : null,
          respiratoryrate: fu.respiratoryrate ? Number(fu.respiratoryrate) : null,
          temperature: fu.temperature ? Number(fu.temperature) : null,
          temperaturelocation: fu.temperaturelocation || "",
          temperatureunit: fu.temperatureunit || "",
          description: fu.description || "",
          nextfollowuprequired: fu.nextfollowuprequired === "yes",
        };

        const res: any = await UpdateFollowupMutation.mutateAsync({
          adverseeventid: Number(id),
          updatereason: payload.reason,
          followup: followupPayload,
        });

        if (fu.nextfollowuprequired !== "yes") {
          const localFollowups = getValues("followups") || [];
          for (let i = localFollowups.length - 1; i > idx; i--) {
            removeFollowup(i);
          }
        }

        await followupQuery.refetch();
        showToastMessage("success", res?.message || "Follow-up updated successfully");
      } catch (err: any) {
        setValue(`followups.${idx}.nextfollowuprequired`, "yes");
        showToastMessage(
          "error",
          err?.response?.data?.message || err?.data?.message || err?.message || err || "Failed to update follow-up"
        );
      } finally {
        setSavingCardIndex(null);
      }
      return;
    }

    if (currentStepToSave === 2) {
      setCurrentStepToSave(null);
      setShowReasonModal(false);

      const formattedFollowups = (pendingFormData.followups || []).map((fu: any) => {
        const ensureSeconds = (timeStr: string) => {
          if (!timeStr) return null;
          return timeStr.length === 5 ? `${timeStr}:00` : timeStr;
        };

        return {
          followupid: fu.followupid ? Number(fu.followupid) : null,
          followupdate: DateFormat(fu.folloupdate) || null,
          starttime: ensureSeconds(fu.starttime),
          endtime: ensureSeconds(fu.endtime),
          bloodpressure: fu.bloodpressure || "",
          pulse: fu.pulse ? Number(fu.pulse) : null,
          respiratoryrate: fu.respiratoryrate ? Number(fu.respiratoryrate) : null,
          temperature: fu.temperature ? Number(fu.temperature) : null,
          temperaturelocation: fu.temperaturelocation || "",
          temperatureunit: fu.temperatureunit || "C",
          description: fu.description || "",
          nextfollowuprequired: fu.nextfollowuprequired === "yes",
        };
      });

      const step2Payload = {
        adverseeventid: Number(id),
        updatereason: payload.reason,
        followups: formattedFollowups,
        deletedfollowupids: deletedFollowupIds,
      };

      try {
        const res: any = await UpdateFollowupMutation.mutateAsync(step2Payload);
        showToastMessage("success", res?.message || "Follow-ups updated successfully");
        setIsFollowupsSaved(true);
      } catch (err: any) {
        showToastMessage("error", err?.response?.data?.message || err?.data?.message || err?.message || "Failed to update follow-ups");
      }
      return;
    }

    if (currentStepToSave === 3) {
      setCurrentStepToSave(null);
      setShowReasonModal(false);

      const getTzOffset = () => {
        const offsetMinutes = new Date().getTimezoneOffset();
        const absOffset = Math.abs(offsetMinutes);
        const hours = String(Math.floor(absOffset / 60)).padStart(2, "0");
        const minutes = String(absOffset % 60).padStart(2, "0");
        const sign = offsetMinutes <= 0 ? "+" : "-";
        return `${sign}${hours}:${minutes}`;
      };
      const offset = getTzOffset();

      const resDate = pendingFormData.resolutiondate ? `${DateFormat(pendingFormData.resolutiondate)}T00:00:00Z` : "";
      const resTime = pendingFormData.resolutiontime ? (pendingFormData.resolutiontime.length === 5 ? `${pendingFormData.resolutiontime}:00` : pendingFormData.resolutiontime) : "";
      const isConsumed = pendingFormData.consumedmedication === "yes" ? "Yes" : (pendingFormData.consumedmedication === "no" ? "No" : "NA");

      const formattedMedicines = (pendingFormData.medicines || []).map((med: any) => {
        const item: any = {
          brandname: med.brandname || "",
          genericname: med.genericname || "",
          frequency: med.frequency || "",
          route: med.route ? Number(med.route) : null,
          startdate: med.startdate ? `${DateFormat(med.startdate)}T00:00:00Z` : "",
          enddate: med.enddate ? `${DateFormat(med.enddate)}T00:00:00Z` : "",
          interactionwithip: med.interaction || med.interactionwithip || "N/A",
          resolutiondate: resDate,
          resolutiontime: resTime,
          medicineconsumed: isConsumed,
        };
        if (med.medicineid) {
          item.medicineid = Number(med.medicineid);
        }
        return item;
      });

      const medsPayload: any = {
        adverseeventid: Number(id),
        updatereason: payload.reason,
        medicines: formattedMedicines,
      };

      if (deletedMedicineIds.length > 0) {
        medsPayload.deletedmedicineids = deletedMedicineIds;
      }

      try {
        const res: any = await UpdateMedicationsMutation.mutateAsync(medsPayload as any);
        showToastMessage("success", res?.message || "Adverse Event Medicines record updated successfully!");
        await Promise.all([
          SelectByIdQuery.refetch(),
          medicationsQuery.refetch(),
        ]);
      } catch (err: any) {
        showToastMessage("error", err?.response?.data?.message || err?.data?.message || err?.message || "Failed to update medications");
      }
      return;
    }

    if (currentStepToSave === 4) {
      setCurrentStepToSave(null);
      setShowReasonModal(false);

      if (!pendingFormData) return;

      const assessmentPayload = {
        adverseeventid: Number(id),
        updatereason: payload.reason,
        assessment: {
          finaldiagnosis: pendingFormData.finaldiagnosis || "",
          causality: pendingFormData.picausality ? Number(pendingFormData.picausality) : null,
          outcomeofevent: pendingFormData.outcomeofevent ? Number(pendingFormData.outcomeofevent) : null,
          likelihood: pendingFormData.likelihood ? Number(pendingFormData.likelihood) : null,
          relationship: pendingFormData.relationship ? Number(pendingFormData.relationship) : null,
        },
      };

      try {
        const res: any = await UpdateAssessmentMutation.mutateAsync(assessmentPayload);
        showToastMessage("success", res?.message || "PI Assessment updated successfully!");
        await assessmentQuery.refetch();
        navigate(MASTER_AE_PATHS.LIST);
      } catch (err: any) {
        showToastMessage("error", err?.response?.data?.message || err?.data?.message || err?.message || "Failed to update PI Assessment");
      }
      return;
    }

    if (!pendingFormData) return;

    const user = GetUserData() as any;

    const finalPayload = formatUpdateAdverseEventPayload(
      pendingFormData,
      Number(id),
      user,
      payload.reason,
      studyOptions,
      DateFormat
    );

    UpdateMutation.mutate(finalPayload as any, {
      onSuccess: async (res: any) => {
        showToastMessage("success", res?.message || "Saved successfully");
        setShowReasonModal(false);

        await Promise.all([
          SelectByIdQuery.refetch(),
          followupQuery.refetch(),
        ]);

        if (currentStepToSave === 1) {
          setIsEventDetailsSaved(true);
        }
      },

      onError: (err: any) => {
        showToastMessage(
          "error",
          err || "Failed to update Adverse Event details"
        );

        // Optional: reset UI if backend rejected the change
        setValue("isfollowuprequired", "yes", {
          shouldValidate: true,
          shouldDirty: true,
        });

        setShowReasonModal(false);
      },
    });
  };

  /* ── Study dropdown ── */
  const { DropDownAllQuery: studySelectAllQuery } = useMasterStudyService({ enabledDropDown: true });
  const studyOptions = useMemo(
    () =>
      (studySelectAllQuery.data as any)?.data?.map((study: any) => ({
        label: study.studycode,
        value: Number(study.studyid),
        studycode: study.studycode,
      })) || [],
    [studySelectAllQuery.data]
  );

  /* ── Study details and Period options ── */
  const { SelectQuery: studyDetailQuery } = useMasterStudyService({
    id: selectedStudyId ? Number(selectedStudyId) : null,
    enabledSelect: !!selectedStudyId,
  });

  const periodOptions = useMemo(() => {
    const studyDetail = (studyDetailQuery.data as any)?.data?.[0];
    if (studyDetail?.periods && Array.isArray(studyDetail.periods)) {
      return studyDetail.periods.map((p: any) => {
        const num = p.period_number || p.period_no || p.periodorder || 1;
        const formattedNum = String(num).padStart(2, "0");
        return {
          label: formattedNum,
          value: formattedNum,
        };
      });
    }
    return [];
  }, [studyDetailQuery.data]);

  /* ── Subjects dropdown ── */
  const { SelectAllQuery: subjectsQuery } = useVolunteerEnrollmentService({
    studyid: selectedStudyId ? Number(selectedStudyId) : null,
  });

  const subjectsOptions = useMemo(() => {
    const list = (subjectsQuery?.data as any)?.data || [];

    return list
      .filter((item: any) => item.replacementstatus?.toLowerCase() !== "replaced")
      .map((item: any) => ({
        label: item.subjectno,
        value: item.subjectno,
        createddate: item.createddate,
      }));
  }, [subjectsQuery?.data]);

  useEffect(() => {
    const data = SelectByIdQuery.data?.data?.[0];
    if (!data) return;
    if (!subjectsOptions.length) return;

    setValue("subjectno", data.subjectno);
  }, [subjectsOptions, SelectByIdQuery.data, setValue]);

  const watchedSubjectNo = watch("subjectno");
  const selectedSubject = useMemo(() => {
    return subjectsOptions.find((s: any) => s.value === watchedSubjectNo);
  }, [watchedSubjectNo, subjectsOptions]);

  const enrollmentDate = selectedSubject?.createddate;

  const watchedOnsetOfEvent = watch("onsetofevent");
  const watchedIsFollowupRequired = watch("isfollowuprequired");

  /* ── Render State ── */
  const fieldState = {
    studyOptions,
    subjectsOptions,
    periodOptions,

    isEditMode: true,
    selectedStudyId,
    isSubjectLoading: subjectsQuery.isLoading,
    getValues,
    watchedTemperatureUnit,
    watchedTemperatureLocation,
    enrollmentDate,
    watchedOnsetOfEvent,
    watchedIsFollowupRequired,
  };

  if (SelectByIdQuery.isLoading || SelectByIdQuery.isFetching) {
    return (
      <Container fluid className={`px-0 ${formStyles.pageContainer}`} style={{ minHeight: "100vh" }}>
        <FullPageLoader />
      </Container>
    );
  }

  const studyFields = getAdverseEventFields("study", fieldState);
  const examFields = getAdverseEventFields("examination", fieldState);

  return (
    <div
      className="page-content"
      style={{
        backgroundColor: "#f4f6fc",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {(UpdateMutation.isPending ||
        InsertFollowupMutation.isPending ||
        UpdateFollowupMutation.isPending ||
        followupQuery.isFetching ||
        SelectByIdQuery.isFetching ||
        isTabChanging) && (
          <FullPageLoader
            message={
              UpdateMutation.isPending ||
                InsertFollowupMutation.isPending ||
                UpdateFollowupMutation.isPending
                ? "Updating Adverse Event Details..."
                : "Loading details..."
            }
          />
        )}
      <Container fluid className="d-flex flex-column flex-grow-1">
        {/* ── Page Header ─────────────────────────────────────────────── */}
        <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
          <div>
            <h3 className="fw-700 mb-1">Edit Adverse Event Record</h3>
            {data && (
              <p className="text-muted mb-0">
                Adverse Event No: {data.adverseeventnumber || id} | Study Code:{" "}
                {data.studycode} | Subject No: {data.subjectno}
              </p>
            )}
          </div>
        </div>

        {/* ── Stepper Switcher ─────────────────────────────────────────────── */}
        <AdverseEventStepper
          activeTab={activeTab}
          isEventDetailsSaved={isEventDetailsSaved}
          onStepClick={setActiveTab}
          isEditMode={true}
        />

        {/* ── Tab Content ──────────────────────────────────────────────── */}
        {activeTab === "event_details" && (
          <AdverseEventEventDetailsForm
            control={control}
            register={register}
            setValue={setValue}
            watch={watch}
            getValues={getValues}
            errors={errors}
            clearErrors={clearErrors}
            studyOptions={studyOptions}
            subjectsOptions={subjectsOptions}
            periodOptions={periodOptions}
            isSubjectLoading={subjectsQuery.isLoading}
            onSubmit={handleSubmit(handleEventDetailsSubmit)}
            isPending={UpdateMutation.isPending}
            onCancel={() => navigate(MASTER_AE_PATHS.DASHBOARD)}
            isSaved={false}
            onNext={() => setActiveTab("followups")}
            isEditMode={true}
          // hasSavedFollowups={hasSavedFollowups}
          />
        )}
        {activeTab === "followups" && (
          <AdverseEventFollowupsForm
            control={control}
            setValue={setValue}
            watch={watch}
            getValues={getValues}
            errors={errors}
            clearErrors={clearErrors}
            trigger={trigger}
            onSubmit={(e: any) => e.preventDefault()}
            onBack={() => setActiveTab("event_details")}
            onCancel={() => navigate(MASTER_AE_PATHS.DASHBOARD)}
            isSaved={isFollowupsSaved}
            onNext={() => setActiveTab("medications")}
            isEditMode={true}
            onSaveCard={handleSaveCard}
            savingCardIndex={savingCardIndex}
            onDeleteFollowup={(followupId, index, onConfirm) => {
              setDeleteType("followup");
              setFollowupIdToDelete(followupId);
              setFollowupIndexToRemove(index);
              setConfirmDeleteCallback(() => onConfirm);
              setDeleteModalOpen(true);
            }}
            fields={followupFields}
            append={appendFollowup}
            remove={removeFollowup}
            isFollowupRequired={watchedIsFollowupRequired}
          />
        )}
        {activeTab === "medications" && (
          <AdverseEventMedicationsForm
            control={control}
            setValue={setValue}
            watch={watch}
            getValues={getValues}
            errors={errors}
            clearErrors={clearErrors}
            onSubmit={handleSubmit(handleMedicationsSubmit)}
            onBack={() => setActiveTab("followups")}
            onNext={() => setActiveTab("pi_assessment")}
            onCancel={() => navigate(MASTER_AE_PATHS.DASHBOARD)}
            isPending={UpdateMedicationsMutation.isPending}
            isEditMode={true}
            fields={medicineFields}
            append={appendMedicine}
            remove={removeMedicine}
            onDeleteMedicine={(index, medicineId) => {
              if (!medicineId) {
                removeMedicine(index);
                return;
              }
              setDeleteType("medicine");
              setFollowupIdToDelete(medicineId);
              setFollowupIndexToRemove(index);
              setConfirmDeleteCallback(() => () => {
                removeMedicine(index);
              });
              setDeleteModalOpen(true);
            }}
          />
        )}
        {activeTab === "pi_assessment" && (
          <AdverseEventPiAssessmentForm
            control={control}
            setValue={setValue}
            watch={watch}
            getValues={getValues}
            errors={errors}
            clearErrors={clearErrors}
            onSubmit={handleSubmit(handlePiAssessmentSubmit)}
            onBack={() => setActiveTab("medications")}
            onCancel={() => navigate(MASTER_AE_PATHS.DASHBOARD)}
            isPending={UpdateAssessmentMutation.isPending}
            isEditMode={true}
            severityname={(() => {
              const rec = Array.isArray(assessmentQuery.data?.data)
                ? assessmentQuery.data?.data[0]
                : assessmentQuery.data?.data;
              return rec?.severityname ?? null;
            })()}
            actiontakenname={(() => {
              const rec = Array.isArray(assessmentQuery.data?.data)
                ? assessmentQuery.data?.data[0]
                : assessmentQuery.data?.data;
              return rec?.actiontakenname ?? null;
            })()}
          />
        )}
      </Container>
      <Reason
        isOpen={showReasonModal}
        toggle={() => setShowReasonModal(false)}
        action="Update"
        id={Number(id)}
        click={handleReasonSubmit}
        isLoading={UpdateMutation.isPending}
      />
      <DeleteModal
        isopen={deleteModalOpen}
        recordId={followupIdToDelete}
        toggle={() => setDeleteModalOpen(false)}
        onDeleteClick={deleteType === "medicine" ? handleDeleteMedicine : handleDeleteFollowup}
        isLoading={DeleteFollowupMutation.isPending || UpdateMedicationsMutation.isPending}
        showDeleteReason={showDeleteReasonModal}
        toggleDeleteReason={() => setShowDeleteReasonModal((prev) => !prev)}
      />
    </div>
  );
};

export default EditAdverseEvent;
